import { Dispatch, SetStateAction, useState } from "react";
import { Dropzone, DropzoneSpanish, ExtFile, FileMosaic } from "@files-ui/react";
import { CircularProgress } from '@mui/joy';
import { CancelButton } from './CancelButton';
import { useUploadEmpresaFileMutation } from '../../api/empresa/empresaApi';
import CloseRoundedIcon from '@mui/icons-material/CloseRounded';
import CheckCircleRoundedIcon from '@mui/icons-material/CheckCircleRounded';
import FileUploadRoundedIcon from '@mui/icons-material/FileUploadRounded';
import { getErrorMessage } from "../../utils/errorHelpers";
import { ErrorMessage } from "./ErrorMessage";

interface Props {
    setOpenModal: Dispatch<SetStateAction<boolean>>;
}

export function UploadEmpresaFileForm({ setOpenModal }: Props) {
    const [files, setFiles] = useState<ExtFile[]>([]);
    const [uploadFile, { isLoading, isSuccess }] = useUploadEmpresaFileMutation()
    const [errorMessage, setErrorMessage] = useState<string | null>(null);
    const [validationErrores, setValidationErrors] = useState<string[]>([])
    const [totalEmpresasRegistradas, setTotalEmpresasRegistradas] = useState(0);

    const handleUploadFile = async () => {
        try {
            if(files.length <= 0)
                return;

            const file = files[0].file;
            if(file == undefined)
                return;

            const response = await uploadFile(file).unwrap();

            setValidationErrors(response.erroresValidacion);
            setTotalEmpresasRegistradas(response.totalEmpresasRegistradas);

            setFiles([]);
            
        } catch (error) {
            setErrorMessage(getErrorMessage(error));
        }
    }

    const updateFiles = (incommingFiles: ExtFile[]) => {
        setFiles(incommingFiles);
    };

    const removeFile = (id: number | string | undefined) => {
        setFiles(files.filter((x) => x.id !== id));
    };

    return (
            <div className="bg-white w-[650px] rounded">
                <div className="flex justify-end bg-gray-100 rounded">
                    <button onClick={() => setOpenModal(false)}>
                        <CloseRoundedIcon  />
                    </button>
                </div>
                {isSuccess
                ?
                <div className="flex items-center h-[350px] justify-center flex-col gap-y-4 p-3">
                    <div>
                        <div className="flex gap-x-2">
                            <p className="text-3xl text-center font-semibold">El archivo se ha subido con exito</p>
                            <div className="flex items-center justify-center">
                                <CheckCircleRoundedIcon sx={{ height: 30, width: 30, color: "green"}}/>
                            </div>
                        </div>
                        <p className="font-semibold text-center">Total de empresas registradas {totalEmpresasRegistradas}</p>
                    </div>

                    {validationErrores.length > 0 && (
                        <>
                        <p className="font-semibold">Se encontraron errores de validacion (estas filas no se han guardado):</p>
                        <div className="h-full overflow-auto">
                            {validationErrores.map((error, index) => (
                                <div key={index} className="my-2">
                                    <p ><span className="font-semibold">Error: </span>{error}</p>
                                </div>
                            ))}
                        </div>
                        </>
                    )}
               </div>
                :<>
                <div className="h-full p-3">
                    <Dropzone
                        onChange={updateFiles}
                        value={files}
                        maxFiles={1}
                        localization={"ES-es"}
                        accept="application/vnd.openxmlformats-officedocument.spreadsheetml.sheet">
                        {files.map((file) => (
                            <FileMosaic key={file.id} {...file} onDelete={removeFile} info />
                        ))}
                    </Dropzone>
                </div>

                {errorMessage && (
                    <div className="my-2">
                        <ErrorMessage
                        customMessage>
                            <p className="text-sm">{errorMessage}</p>
                        </ErrorMessage>
                    </div>
                )}

                <div className="flex gap-x-2 p-[16px]">
                    {isLoading
                    ? 
                        <div className="w-[105px] h-[30px] flex items-center justify-center">
                            <CircularProgress sx={{
                                "--CircularProgress-progressThickness": "4px",
                                "--CircularProgress-trackThickness": "4px",
                                "--CircularProgress-size": "25px"
                            }}/>
                        </div>
                    :
                    <>
                    <button
                        onClick={handleUploadFile}
                        className="bg-green-800 h-[30px] rounded-[3px] px-[6px] py-[4px] flex gap-1 items-center">
                        <FileUploadRoundedIcon sx={{ height: 15, width: 15, color: "white"}}/>
                        <p className="text-white text-[13px] font-semibold">Subir Archivo</p>
                    </button>

                    <CancelButton
                        onClick={()=> {
                            setOpenModal(false);
                        }}/>
                    </>}
                </div>
                </>}
            </div>
    );
}